import { useEffect, useState } from "react";
import { atStation } from "../../../../../util/stationTime";
import { zoneOfLocationId } from "../../../../../util/locationTimeZone";
import type { LocationPros } from "../../../../../types/baseTypes";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "../../../../../redux/store";
import { Spinner } from "../../../../../components/ui/spinner";
import { Button } from "../../../../../components/ui/button";
import NoResult from "../../../../layout/components/NoResult";
import FullScreenDialog from "../../../../common/FullScreenDialog";
import ApartmentListCard from "../ApartmentRental/ApartmentListCard";
import MapView from "../ApartmentRental/MapView";
import ObjectSearchFailure from "./ObjectSearchFailure";
import {
  navigatedToPreviousStep,
  setNextButtonClick,
  setSelectedApartment,
  clearSelectedApartment,
  setIsMapOpen,
} from "../../../../../redux/slice/apartmentRental/apartmentRentalSlice";
import {
  endTimeSelected,
  setSelectedLocation,
  startTimeSelected,
} from "../../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { Apartment } from "../../../../../types/apartments";
import { selectObjectSearchPending } from "../../objectSearchPending";
import {
  parseBookingQuery,
  stringifyBookingQuery,
} from "../../url/bookingQuery";
import {
  ArchivedCategoryState,
  CategoryNoticeKeys,
  UnknownCategoryNote,
} from "./CategoryNotice";

const NO_LOCATIONS: LocationPros[] = [];

/**
 * The step where the customer picks the thing they are booking — the room, the
 * venue, whatever the tenant's ProductCategory happens to be (ADR-0026).
 *
 * Merged from the two copies under `roomRental/` and `venueReservation/`, which
 * after slice C differed in exactly two things. Both are props here rather than
 * branches, so the component itself holds no flow knowledge.
 *
 * ── The loading predicate ─────────────────────────────────────────────────
 *
 * Two independent reasons this screen cannot show a result yet, covering
 * different moments, so it needs BOTH:
 *
 * - `loader` covers the search: the fetch flow sets it true beside
 *   `apartmentsLoadStarted()` and false on every exit path.
 * - `!searchAnswered` covers the frame BEFORE that. The parent page starts the
 *   search from a `useEffect`, which React runs after it paints, so a cold
 *   mount at this step gets one painted frame with `loader` still false and the
 *   list still `[]` — and `[]` renders `NoResult`. Without this the customer
 *   sees "no results" flash before the spinner.
 *
 * `locationApartmentList` cannot answer the second question on its own: it is
 * initialised to `[]` and typed non-optional, so "nothing matched" and "nobody
 * has searched" are the same value. The old `_.isEmpty(list)` guard tried and
 * was dead by construction.
 */
export interface ObjectSelectionStepSharedProps {
  /**
   * What to say when the search failed. A failure must never render as an
   * empty list (#1970), and the wording is flow-specific — "Die verfügbaren
   * Zimmer …" is wrong on a venue search.
   */
  errorMessageKey: string;
  /**
   * Re-point `selectedLocation` at the chosen object's own location when the
   * customer searched across "all districts".
   *
   * ⚠️ NOT a settled design decision — it is the one asymmetry the two copies
   * had, carried across as-is so that merging them changes no behaviour in
   * either flow. Room rental did this; venue reservation did not, and whether
   * that is a defect there is unestablished: in the venue flow
   * `selectedLocation` is read only for `nextEnabled`. Being measured on
   * staging as #2764 E14 — Neuland has several locations across Wolfsburg
   * districts, so it is observable. When the answer lands this becomes one
   * line, not a re-do.
   */
  reselectLocationOnPick?: boolean;
  /**
   * The flow-specific sentences for a `category` key the server reported as
   * archived or unknown (#2764 F9/F10) — the noun differs per flow.
   */
  categoryNoticeKeys: CategoryNoticeKeys;
  /**
   * Re-run this flow's search. Used by "show all" on an archived category,
   * after the key has been taken out of the URL — the search reads it from
   * there. The shell only searches on step changes, and this is not one.
   */
  searchAgain: () => Promise<unknown>;
  /**
   * Object-first (#3035): the room the customer already picked. The dated
   * search still runs exactly as it does for everyone — its capacity,
   * opening-hours and booking rules decide — and only that room's card is
   * shown: priced for the chosen time, or with its opening-hours hint, or, if
   * the search did not return it, the notice that it is not free then.
   */
  onlyObjectId?: string | null;
  /**
   * That room's capacity, when known. The dated search drops a room the head
   * count does not fit, and without this the screen would say "not free then"
   * for a room that is free but too small — a count kept from a bigger room
   * the customer looked at first is the realistic way there.
   */
  onlyObjectCapacity?: number | null;
}

function ObjectSelectionStepShared({
  errorMessageKey,
  reselectLocationOnPick = false,
  categoryNoticeKeys,
  searchAgain,
  onlyObjectId = null,
  onlyObjectCapacity = null,
}: Readonly<ObjectSelectionStepSharedProps>) {
  const { locationApartmentList: searchResult, isMapOpen, categoryNotice } = useSelector(
    (state: RootState) => state.apartment
  )!;
  const numberOfGuests = useSelector((state: RootState) => state.apartment.numberOfGuest);
  const tooManyPeople =
    !!onlyObjectCapacity && numberOfGuests > onlyObjectCapacity;
  const locationApartmentList = onlyObjectId
    ? searchResult.filter((apartment: Apartment) => apartment.id === onlyObjectId)
    : searchResult;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Drop the archived key and search again: "Alle … anzeigen". `replace`, so
  // Back does not return the customer to an offer that no longer exists.
  const showAllObjects = async () => {
    const query = parseBookingQuery(location.search);
    delete query.category;
    navigate(
      { pathname: location.pathname, search: stringifyBookingQuery(query) },
      { replace: true }
    );
    await searchAgain();
  };

  // One definition, shared with the page shell — see `selectObjectSearchPending`.
  const searchPending = useSelector(selectObjectSearchPending);
  // A failed search leaves the same empty list as one that genuinely matched
  // nothing, and reads as "nothing here" (#1970).
  const loadFailed = useSelector(
    (state: RootState) => state.apartment.apartmentsLoadFailed
  );

  const district = useSelector(
    (state: RootState) => state.bookingCreation.roomRentalLocation.district
  );
  const locations = useSelector(
    (state: RootState) => state.booking.useCaseLocations
  );
  // Every location of the tenant (`GET location`, loaded at app start), for
  // the zone of each listed room's location (#3219).
  const allLocations = useSelector(
    (state: RootState) => state.booking?.locations ?? NO_LOCATIONS
  );
  const zoneOfApartment = (apartment: Apartment): string =>
    zoneOfLocationId(allLocations, apartment.locationId);

  useEffect(() => {
    if (!isMapOpen) {
      dispatch(clearSelectedApartment());
    }
  }, [dispatch, isMapOpen]);

  // "Zeit auf 8:00–12:00 anpassen" (PO, 2026-10-02): take over the window a
  // venue's opening hours allow, then search again. The search runs from an
  // effect, not from the click handler: `searchAgain` reads the times this
  // render captured, so it has to be the NEXT render's copy — the one that
  // already sees the adjusted times. The times reach the URL through the
  // usual Redux → URL sync.
  const [searchAfterAdjust, setSearchAfterAdjust] = useState(false);
  useEffect(() => {
    if (!searchAfterAdjust) return;
    setSearchAfterAdjust(false);
    void searchAgain();
  }, [searchAfterAdjust, searchAgain]);

  const handleAdjustTime = (apartment: Apartment) => {
    const fit = apartment.openingHoursFit;
    if (!fit) return;
    // Start first: the end reducer rejects an end not after the start.
    // Read on the station's clock — the room's location zone (#3219): the
    // picked times are station wall-clock (`setTimeOnDate`), and the button
    // label and the hint beside it are too.
    const timeZone = zoneOfApartment(apartment);
    dispatch(startTimeSelected({ startTime: atStation(fit.adjustedStart, timeZone).format("H:mm") }));
    dispatch(endTimeSelected({ endTime: atStation(fit.adjustedEnd, timeZone).format("H:mm") }));
    setSearchAfterAdjust(true);
  };

  const handleSelectApartment = (apartment: Apartment) => {
    dispatch(setSelectedApartment(apartment));

    if (reselectLocationOnPick && district === "all") {
      const chosenLocation = locations.find(
        (loc: { id: string }) => loc.id === apartment.locationId
      );
      // The `!` is carried over from the room copy DELIBERATELY, not
      // overlooked. `useCaseLocations` and the object list are fetched
      // separately, so a miss dispatches `undefined` and strands the next step
      // on a gate it cannot pass. Guarding it would be an improvement — and
      // would change room-rental behaviour inside a merge that is supposed to
      // change none, and would also MASK the miss: the customer would proceed
      // carrying a stale location instead of visibly stopping. #2764 E14 is
      // measuring this exact path right now; the guard is a decision for when
      // that comes back, together with whether this block belongs here at all.
      dispatch(setSelectedLocation(chosenLocation!));
    }

    dispatch(setNextButtonClick());
  };

  // Order matters: a failure outranks a still-loading state, and both outrank
  // the list — otherwise the customer reads an empty result as "nothing here".
  if (loadFailed) {
    return <ObjectSearchFailure errorMessageKey={errorMessageKey} />;
  } else if (searchPending) {
    /**
     * IN FLOW, not an overlay — QA #2764 E10/E11 on integration.
     *
     * This used to render `LoaderOverlay` (a MUI `Backdrop`, `position:
     * fixed`), and on the deployed build it landed exactly one viewport below
     * the fold: measured `backdropTop: 820` in an `820px` viewport, so during
     * every search the customer saw NO loading indicator — just the results
     * header's zero state over an empty list, i.e. the no-results screen, for
     * 400–600 ms.
     *
     * A spinner that sits where the list will be cannot be positioned off
     * screen by whatever containing block the dialog establishes, so this
     * sidesteps the question rather than betting on a diagnosis. (The obvious
     * guess — the dialog's `slide-in-from-bottom` transform — was tested in
     * Chromium and does NOT explain it: during that animation the whole dialog
     * is translated, header included, and QA saw the header. The backdrop's
     * real cause was never found; `LoaderOverlay` now renders into <body> for
     * all its call sites instead (#2807).)
     */
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner size="lg" ariaLabel={t("common.loading")} />
      </div>
    );
  } else {
    // Not `NoResult` for an archived category: that one says "nothing matches
    // your search" and offers to edit it, which no date change could fix here.
    // Object-first (#3035): the one room the customer picked did not come
    // back for this time — said as such, with the way back to the time.
    const notFreeThen = (
      <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
        <p className="text-sm text-foreground/80">
          {tooManyPeople
            ? t("book_location.object_first.too_many_people", { count: onlyObjectCapacity })
            : t("book_location.object_first.not_available")}
        </p>
        <Button variant="outline" onClick={() => dispatch(navigatedToPreviousStep())}>
          {t("book_location.object_first.change_time")}
        </Button>
      </div>
    );
    const searchEmptyState =
      categoryNotice?.kind === "archived" ? (
        <ArchivedCategoryState
          notice={categoryNotice}
          keys={categoryNoticeKeys}
          onShowAll={showAllObjects}
        />
      ) : (
        <NoResult />
      );
    const emptyState = onlyObjectId ? notFreeThen : searchEmptyState;
    return (
      <div
        style={{
          maxHeight: "98%", // Set your desired max height
          overflowY: "auto", // Enable vertical scrolling
          marginLeft: "16px", // Add margin to the left
          marginRight: "16px", // Add margin to the right
        }}
      >
        {categoryNotice?.kind === "unknown" && (
          <UnknownCategoryNote keys={categoryNoticeKeys} />
        )}
        {locationApartmentList?.length >= 1 ? (
          <>
            {locationApartmentList.map((apartment: Apartment) => (
              // Keyed by identity. `description` is not unique — two objects
              // sharing one blurb collapsed onto a single React key, so
              // selecting either re-rendered the wrong row.
              <div
                key={
                  apartment.id ||
                  apartment.assetId ||
                  `${apartment.name}-${apartment.locationId}`
                }
              >
                <ApartmentListCard
                  data={apartment}
                  onSelectApartment={handleSelectApartment}
                  onAdjustTime={handleAdjustTime}
                  timeZone={zoneOfApartment(apartment)}
                />
              </div>
            ))}
          </>
        ) : (
          emptyState
        )}

        <FullScreenDialog
          open={isMapOpen}
          className="max-w-full bg-transparent"
          onClose={() => dispatch(setIsMapOpen(false))}
          ariaLabel={t("common.dialog.map")}
        >
          <MapView />
        </FullScreenDialog>
      </div>
    );
  }
}

export default ObjectSelectionStepShared;
